import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import AccountMenu from 'components/account/AccountMenu';

import { useObservability } from '../../../../../helpers/observability';
import { useLargerThanSM } from '../useLargerThanSM';
import { redirectToLogin } from './constants';

interface GlassAccountSectionProps {
  isLoggedIn: boolean;
  showLogin: boolean;
  initials: string[];
}

export const GlassAccountSection: React.FC<GlassAccountSectionProps> = ({
  isLoggedIn,
  showLogin,
  initials,
}) => {
  const { trackEvent, trackJourneyEvent } = useObservability();
  const largerThanSM = useLargerThanSM();
  const [isExpanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = useCallback(() => setExpanded((prev) => !prev), []);
  const closeDropdown = useCallback(() => setExpanded(false), []);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDropdown();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded, closeDropdown]);

  const handleAvatarPress = useCallback(() => {
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'user-account-menu-click' });
    toggleDropdown();
  }, [trackEvent, trackJourneyEvent, toggleDropdown]);

  const handleLoginPress = useCallback(() => {
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'account-section-click' });
    redirectToLogin();
  }, [trackEvent, trackJourneyEvent]);

  if (isLoggedIn) {
    return (
      <Box ref={containerRef} className="relative flex items-center gap-3 max-md:hidden">
        <Box
          onClick={handleAvatarPress}
          analyticsName="cosmos-glass-nav-account"
          aria-label="User account menu"
          aria-haspopup="true"
          aria-expanded={isExpanded}
          className="cursor-pointer border-0 bg-transparent p-0"
        >
          <Box className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-od-bg-primary/20">
            <Text as="span" className="text-label-sm font-medium text-od-content-inverse-primary">
              {initials}
            </Text>
          </Box>
        </Box>
        {largerThanSM && <AccountMenu isExpanded={isExpanded} isLoggedIn={isLoggedIn} />}
      </Box>
    );
  }

  return (
    <Row className="pl-12 items-center gap-3 max-md:hidden">
      {showLogin && (
        <Box
          onClick={handleLoginPress}
          analyticsName="cosmos-glass-nav-signin"
          aria-label="Sign in to your account"
          className="cursor-pointer rounded-xl border border-od-content-inverse-primary bg-transparent px-4 py-2.5 text-od-content-inverse-primary! no-underline transition-[background-color,border-color] duration-200 hover:bg-od-bg-primary/10"
        >
          <Text as="span" className="text-label-md text-inherit">
            Sign in
          </Text>
        </Box>
      )}
    </Row>
  );
};
